import React from "react";
import { Home, Compass, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const DIRECTIONS = [
  { dir: "N",  angle: 0,   element: "Water · जल", deity: "Kubera", best: "Cash locker, water body, main entrance", worst: "Kitchen, toilet" },
  { dir: "NE", angle: 45,  element: "Water + Air", deity: "Ishaan", best: "Pooja room, meditation, well", worst: "Bedroom, kitchen, staircase" },
  { dir: "E",  angle: 90,  element: "Air · वायु", deity: "Indra", best: "Main entrance, dining, living room", worst: "Toilet, storage" },
  { dir: "SE", angle: 135, element: "Fire · अग्नि", deity: "Agni", best: "Kitchen, electronics, generator", worst: "Master bedroom, pooja" },
  { dir: "S",  angle: 180, element: "Fire", deity: "Yama", best: "Storage, master bedroom (South-West wall)", worst: "Main entrance, well" },
  { dir: "SW", angle: 225, element: "Earth · पृथ्वी", deity: "Nairitya", best: "Master bedroom, heavy items", worst: "Kitchen, entrance, toilet" },
  { dir: "W",  angle: 270, element: "Earth", deity: "Varuna", best: "Dining, children's room, study", worst: "" },
  { dir: "NW", angle: 315, element: "Air", deity: "Vayu", best: "Guest room, storage of grains", worst: "Master bedroom, pooja" },
];

const CENTER = { name: "Brahmasthan · केंद्र", note: "Always keep the centre of your home open, clean and free of clutter. It is the seat of cosmic energy." };

const TIPS = [
  { t: "Main door should ideally face N, E or NE." },
  { t: "Sleep with head towards South or East — never North." },
  { t: "Kitchen belongs to SE (Agni). Cook facing East." },
  { t: "Money locker should open towards North (Kubera)." },
  { t: "Avoid mirrors facing the bed." },
  { t: "Keep toilets in NW or SE — never NE or SW." },
];

export default function VastuService() {
  return (
    <div className="relative min-h-screen">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1512] via-[#050507] to-[#050507]" />
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20">
          <div className="text-xs uppercase tracking-[0.35em] text-[#D4AF37] mb-4">Vastu Shastra · वास्तु</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tighter max-w-3xl">
            The eight <span className="italic gold-gradient">sacred directions</span> of your home.
          </h1>
          <p className="mt-6 max-w-xl text-[#A0A4AB]">
            Align your walls with the cosmos. Balance the five elements. Invite prosperity, health & peace.
          </p>
        </div>
      </div>

      {/* Compass */}
      <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="relative aspect-square max-w-[480px] mx-auto">
            <div className="absolute inset-0 rounded-full border border-[#D4AF37]/30 slow-spin" />
            <div className="absolute inset-6 rounded-full border border-[#D4AF37]/20" />
            <div className="absolute inset-16 rounded-full border border-white/10 reverse-spin" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="font-deva text-4xl text-[#D4AF37]">केंद्र</div>
                <div className="text-[10px] uppercase tracking-widest text-[#A0A4AB] mt-1">Brahmasthan</div>
              </div>
            </div>
            {DIRECTIONS.map((d) => {
              const rad = ((d.angle - 90) * Math.PI) / 180;
              const r = 195;
              const x = 240 + r * Math.cos(rad);
              const y = 240 + r * Math.sin(rad);
              return (
                <div key={d.dir} className="absolute font-serif text-2xl text-[#D4AF37]" style={{ left: x - 15, top: y - 18 }}>
                  {d.dir}
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-6">
          <div className="grid grid-cols-2 gap-3">
            {DIRECTIONS.map((d) => (
              <div key={d.dir} data-testid={`vastu-dir-${d.dir}`} className="p-5 rounded-xl bg-[#0A0C10] border border-white/10 hover:border-[#D4AF37]/40 transition">
                <div className="flex items-center justify-between">
                  <div className="font-serif text-2xl text-[#D4AF37]">{d.dir}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#A0A4AB]">{d.element}</div>
                </div>
                <div className="text-xs text-[#D4AF37] mt-2">Deity: {d.deity}</div>
                <div className="text-xs text-[#A0A4AB] mt-2 leading-relaxed"><span className="text-emerald-400">✓</span> {d.best}</div>
                {d.worst && <div className="text-xs text-[#A0A4AB] mt-1 leading-relaxed"><span className="text-red-400">✗</span> {d.worst}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="border-t border-white/10 bg-[#0A0C10]">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Golden Rules</div>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-10">Six timeless <span className="italic gold-gradient">Vastu rules.</span></h2>
          <div className="grid md:grid-cols-3 gap-3">
            {TIPS.map((t, i) => (
              <div key={i} className="p-6 rounded-xl bg-[#050507] border border-white/10 flex gap-4">
                <div className="font-serif text-3xl gold-gradient">{String(i + 1).padStart(2, "0")}</div>
                <div className="text-sm text-[#EAE6DF] leading-relaxed">{t.t}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex justify-center">
            <Link to="/astrologers" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition">
              <Home size={14}/> Book Vastu Consultation <ArrowUpRight size={14}/>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
